// ServiceManagementPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { FaSearch, FaPlus, FaTimes, FaFilter, FaThLarge, FaList } from 'react-icons/fa';
import { Badge } from 'react-bootstrap';
import HeaderTopSection from "../../components/partials/HeaderTop";
import ServiceHDSModal from '../../components/modals/servicehds/ServiceHDSModal';
import ServiceHDSCreateModal from '../../components/modals/servicehds/ServiceHDSCreateModal';
import SubServiceModal from '../../components/modals/servicehds/SubServiceModal';
import ServiceHDSService from '../../services/ServiceDHSService';
import '../../assets/css/ServiceHDSPage.css';
import Banner from "../../components/partials/Banner";
import Swal from 'sweetalert2';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { formatDateTime } from '../../utils/dateHelper';
import LoadingSpinner from '../../utils/LoadingSpinner';

const ServiceManagementPage = () => {
    const [services, setServices] = useState([]);
    const [filteredServices, setFilteredServices] = useState([]);
    const [selectedService, setSelectedService] = useState(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isSubServiceModalOpen, setIsSubServiceModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [minLoadingTimePassed, setMinLoadingTimePassed] = useState(false);

    // États pour la recherche et le filtrage
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'

    useEffect(() => {
        fetchServices();

        const minLoadingTimer = setTimeout(() => {
            setMinLoadingTimePassed(true);
        }, 3000);

        return () => {
            clearTimeout(minLoadingTimer);
        };
    }, []);

    // Filtrer les services en fonction du terme de recherche
    useEffect(() => {
        if (services.length > 0) {
            const filtered = services.filter(service => {
                const searchLower = searchTerm.toLowerCase().trim();
                if (!searchLower) return true;

                // Recherche dans le nom
                if (service.name.toLowerCase().includes(searchLower)) return true;

                // Recherche dans la description
                if (service.description && service.description.toLowerCase().includes(searchLower)) return true;

                // Recherche dans les sous-services
                if (service.subServices && service.subServices.length > 0) {
                    return service.subServices.some(sub =>
                        sub.name.toLowerCase().includes(searchLower)
                    );
                }

                return false;
            });
            setFilteredServices(filtered);
        } else {
            setFilteredServices([]);
        }
    }, [searchTerm, services]);

    const fetchServices = async () => {
        const startTime = Date.now();

        try {
            setIsLoading(true);
            setMinLoadingTimePassed(false);

            const servicesData = await ServiceHDSService.getAllServices();

            const servicesWithSubServices = await Promise.all(
                servicesData.map(async (service) => {
                    try {
                        const subServices = await ServiceHDSService.getSubServicesByServiceId(service.id);
                        return { ...service, subServices };
                    } catch (error) {
                        console.error(`Error fetching subservices for service ${service.id}:`, error);
                        return { ...service, subServices: [] };
                    }
                })
            );

            setServices(servicesWithSubServices);
            setFilteredServices(servicesWithSubServices);

            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 3000 - elapsedTime);

            if (remainingTime > 0) {
                setTimeout(() => {
                    setIsLoading(false);
                    setMinLoadingTimePassed(true);
                }, remainingTime);
            } else {
                setIsLoading(false);
                setMinLoadingTimePassed(true);
            }
        } catch (error) {
            console.error('Error fetching services:', error);
            toast.error('Erreur lors du chargement des services');

            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 3000 - elapsedTime);

            if (remainingTime > 0) {
                setTimeout(() => {
                    setIsLoading(false);
                    setMinLoadingTimePassed(true);
                }, remainingTime);
            } else {
                setIsLoading(false);
                setMinLoadingTimePassed(true);
            }
        }
    };

    const refreshServices = async () => {
        try {
            setIsLoading(true);

            const servicesData = await ServiceHDSService.getAllServices();

            const servicesWithSubServices = await Promise.all(
                servicesData.map(async (service) => {
                    try {
                        const subServices = await ServiceHDSService.getSubServicesByServiceId(service.id);
                        return { ...service, subServices };
                    } catch (error) {
                        console.error(`Error fetching subservices for service ${service.id}:`, error);
                        return { ...service, subServices: [] };
                    }
                })
            );

            setServices(servicesWithSubServices);
            setFilteredServices(servicesWithSubServices);
            setIsLoading(false);

        } catch (error) {
            console.error('Error refreshing services:', error);
            toast.error('Erreur lors du rafraîchissement des services');
            setIsLoading(false);
        }
    };

    const handleViewService = (service) => {
        setSelectedService(service);
        setIsEditMode(false);
        setIsViewModalOpen(true);
    };

    const handleEditService = (service) => {
        setSelectedService(service);
        setIsEditMode(true);
        setIsViewModalOpen(true);
    };

    const handleAddSubService = (service) => {
        setSelectedService(service);
        setIsSubServiceModalOpen(true);
    };

    const handleDeleteService = async (serviceId, serviceName) => {
        try {
            const result = await Swal.fire({
                title: 'Confirmer la suppression',
                text: `Êtes-vous sûr de vouloir supprimer le service "${serviceName}" ?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#ff4757',
                cancelButtonColor: '#747d8c',
                confirmButtonText: 'Oui, supprimer',
                cancelButtonText: 'Annuler',
                background: '#2f3542',
                color: '#ffffff'
            });

            if (result.isConfirmed) {
                await ServiceHDSService.deleteService(serviceId);
                await refreshServices();
                toast.success('Service supprimé avec succès');
            }
        } catch (error) {
            console.error('Error deleting service:', error);
            toast.error('Erreur lors de la suppression du service');
        }
    };

    const handleCloseModal = () => {
        setIsViewModalOpen(false);
        setSelectedService(null);
    };

    const handleCloseCreateModal = () => {
        setIsCreateModalOpen(false);
    };

    const handleCloseSubServiceModal = () => {
        setIsSubServiceModalOpen(false);
        setSelectedService(null);
    };

    const handleServiceCreated = () => {
        refreshServices();
    };

    const handleSubServiceCreated = () => {
        refreshServices();
    };

    const handleClearSearch = () => {
        setSearchTerm('');
    };

    const getServiceImageUrl = (service) => {
        if (service.mediaUrlPresigned) {
            return service.mediaUrlPresigned;
        }
        if (service.mediaUrl) {
            return service.mediaUrl;
        }
        return null;
    };

    // Compter le nombre total de sous-services
    const totalSubServices = useMemo(() => {
        return services.reduce((acc, service) => acc + (service.subServices?.length || 0), 0);
    }, [services]);

    const showLoading = isLoading || !minLoadingTimePassed;

    return (
        <>
            <HeaderTopSection />
            <Banner title="Gestion des Services" />

            <LoadingSpinner
                isVisible={showLoading}
                loadingText="Chargement des services..."
            />

            <div className="services-modern-container">
                {/* Header amélioré avec recherche */}
                <div className="modern-header">
                    <div className="header-content">
                        <div className="header-text">
                            <h1 className="page-title">Services Professionnels</h1>
                            <p className="page-subtitle">
                                Gérez l'ensemble de vos services et sous-services
                            </p>
                            {/* Statistiques rapides */}
                            <div className="quick-stats d-flex gap-3 mt-2">
                                <Badge bg="primary" className="rounded-pill px-3 py-2">
                                    <strong>{services.length}</strong> Services
                                </Badge>
                                <Badge bg="success" className="rounded-pill px-3 py-2">
                                    <strong>{totalSubServices}</strong> Sous-services
                                </Badge>
                            </div>
                        </div>
                        <div className="header-actions d-flex gap-3 align-items-center">
                            {/* Boutons de changement de vue */}
                            <div className="view-toggle d-flex gap-1">
                                <button
                                    className={`btn btn-sm rounded-pill px-3 ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setViewMode('grid')}
                                    title="Vue en grille"
                                >
                                    <FaThLarge />
                                </button>
                                <button
                                    className={`btn btn-sm rounded-pill px-3 ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setViewMode('list')}
                                    title="Vue en liste"
                                >
                                    <FaList />
                                </button>
                            </div>
                            <button
                                className="modern-create-btn"
                                onClick={() => setIsCreateModalOpen(true)}
                            >
                                <FaPlus className="me-2" />
                                Nouveau Service
                            </button>
                        </div>
                    </div>

                    {/* Barre de recherche */}
                    <div className="search-bar-container mt-3">
                        <div className={`search-wrapper ${isSearchFocused ? 'search-focused' : ''}`}>
                            <FaSearch className="search-icon" />
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Rechercher un service par nom, description ou sous-service..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onFocus={() => setIsSearchFocused(true)}
                                onBlur={() => setIsSearchFocused(false)}
                            />
                            {searchTerm && (
                                <button
                                    className="search-clear-btn"
                                    onClick={handleClearSearch}
                                    title="Effacer la recherche"
                                >
                                    <FaTimes />
                                </button>
                            )}
                            {searchTerm && (
                                <Badge bg="info" className="search-results-badge rounded-pill">
                                    {filteredServices.length} résultat(s)
                                </Badge>
                            )}
                        </div>
                    </div>
                </div>

                {/* Grille des services */}
                <div className="services-grid-section">
                    {!showLoading && filteredServices.length === 0 && searchTerm ? (
                        <div className="empty-modern-state">
                            <div className="empty-icon">🔍</div>
                            <h3>Aucun résultat trouvé</h3>
                            <p>Aucun service ne correspond à votre recherche "{searchTerm}"</p>
                            <button
                                className="btn btn-outline-primary rounded-pill px-4"
                                onClick={handleClearSearch}
                            >
                                <FaTimes className="me-2" />
                                Effacer la recherche
                            </button>
                        </div>
                    ) : services.length === 0 && !showLoading ? (
                        <div className="empty-modern-state">
                            <div className="empty-icon">📋</div>
                            <h3>Aucun service disponible</h3>
                            <p>Commencez par créer votre premier service</p>
                            <button
                                className="modern-create-btn"
                                onClick={() => setIsCreateModalOpen(true)}
                            >
                                <FaPlus className="me-2" />
                                Créer un Service
                            </button>
                        </div>
                    ) : (
                        !showLoading && (
                            <div className={`services-${viewMode}`}>
                                {filteredServices.map(service => {
                                    const imageUrl = getServiceImageUrl(service);
                                    return (
                                        <div key={service.id} className={`service-card ${viewMode === 'list' ? 'service-card-list' : ''}`}>
                                            <div className="service-image-container">
                                                {imageUrl ? (
                                                    <img
                                                        src={imageUrl}
                                                        alt={service.name}
                                                        className="service-image"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            const placeholder = e.target.parentElement.querySelector('.service-image-placeholder');
                                                            if (placeholder) placeholder.style.display = 'flex';
                                                        }}
                                                    />
                                                ) : null}
                                                {!imageUrl && (
                                                    <div className="service-image-placeholder">
                                                        <span className="placeholder-icon">📷</span>
                                                    </div>
                                                )}
                                                {imageUrl && (
                                                    <div className="service-image-placeholder" style={{ display: 'none' }}>
                                                        <span className="placeholder-icon">📷</span>
                                                    </div>
                                                )}
                                                <div className="service-overlay">
                                                    <button
                                                        className="overlay-btn view-btn"
                                                        onClick={() => handleViewService(service)}
                                                        title="Voir les détails"
                                                    >
                                                        👁️
                                                    </button>
                                                    <button
                                                        className="overlay-btn edit-btn"
                                                        onClick={() => handleEditService(service)}
                                                        title="Modifier"
                                                    >
                                                        ✏️
                                                    </button>
                                                    <button
                                                        className="overlay-btn delete-btn"
                                                        onClick={() => handleDeleteService(service.id, service.name)}
                                                        title="Supprimer"
                                                    >
                                                        🗑️
                                                    </button>
                                                </div>
                                                {/* Badge nombre de sous-services */}
                                                {service.subServices && service.subServices.length > 0 && (
                                                    <Badge
                                                        bg="success"
                                                        className="position-absolute top-0 end-0 m-2 rounded-pill"
                                                    >
                                                        {service.subServices.length}
                                                    </Badge>
                                                )}
                                            </div>

                                            <div className="service-content">
                                                <div className="service-header">
                                                    <h3 className="service-name">{service.name}</h3>
                                                    {service.category && (
                                                        <Badge bg="secondary" className="rounded-pill">
                                                            {service.category}
                                                        </Badge>
                                                    )}
                                                </div>

                                                {service.description && (
                                                    <p className="service-description text-muted small">
                                                        {service.description.length > 100
                                                            ? `${service.description.substring(0, 100)}...`
                                                            : service.description}
                                                    </p>
                                                )}

                                                <div className="service-meta">
                                                    <div className="meta-item">
                                                        <span className="meta-label">Sous-services</span>
                                                        <div className="meta-value subservices-list">
                                                            {service.subServices && service.subServices.length > 0 ? (
                                                                <div className="subservices-container">
                                                                    {service.subServices.slice(0, 3).map((subService, index) => (
                                                                        <span
                                                                            key={subService.id || index}
                                                                            className="subservice-badge"
                                                                        >
                                                                            {subService.name}
                                                                        </span>
                                                                    ))}
                                                                    {service.subServices.length > 3 && (
                                                                        <span className="subservice-badge more-badge">
                                                                            +{service.subServices.length - 3}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span className="no-subservices">Aucun sous-service</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Bouton Ajouter Sous-service */}
                                                <div className="sub-service-section">
                                                    <button
                                                        className="add-sub-service-btn"
                                                        onClick={() => handleAddSubService(service)}
                                                    >
                                                        <span className="btn-icon">+</span>
                                                        Ajouter un Sous-service
                                                    </button>
                                                </div>

                                                <div className="service-actions">
                                                    <button
                                                        className="action-btn primary-btn"
                                                        onClick={() => handleViewService(service)}
                                                    >
                                                        Voir Détails
                                                    </button>
                                                    <button
                                                        className="action-btn secondary-btn"
                                                        onClick={() => handleEditService(service)}
                                                    >
                                                        Modifier
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    )}
                </div>
            </div>

            {/* Modals */}
            <ServiceHDSModal
                service={selectedService}
                isOpen={isViewModalOpen}
                onClose={handleCloseModal}
                isEditMode={isEditMode}
                onSave={handleServiceCreated}
            />

            <ServiceHDSCreateModal
                isOpen={isCreateModalOpen}
                onClose={handleCloseCreateModal}
                onServiceCreated={handleServiceCreated}
            />

            <SubServiceModal
                service={selectedService}
                isOpen={isSubServiceModalOpen}
                onClose={handleCloseSubServiceModal}
                onSubServiceCreated={handleSubServiceCreated}
            />

            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="dark"
            />

            {/* Styles CSS intégrés */}
            <style jsx>{`
                .quick-stats .badge {
                    font-size: 0.85rem;
                    font-weight: 500;
                }

                .quick-stats .badge strong {
                    font-weight: 700;
                }

                /* Styles de la barre de recherche */
                .search-bar-container {
                    width: 100%;
                    max-width: 800px;
                    margin: 0 auto;
                }

                .search-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                    background: white;
                    border-radius: 50px;
                    border: 2px solid #e2e8f0;
                    transition: all 0.3s ease;
                    padding: 0 16px;
                }

                .search-wrapper:hover {
                    border-color: #cbd5e0;
                }

                .search-wrapper.search-focused {
                    border-color: #28a745;
                    box-shadow: 0 0 0 4px rgba(40, 167, 69, 0.1);
                }

                .search-icon {
                    color: #a0aec0;
                    font-size: 1rem;
                    margin-right: 12px;
                    flex-shrink: 0;
                }

                .search-input {
                    flex: 1;
                    border: none;
                    padding: 12px 0;
                    font-size: 0.95rem;
                    background: transparent;
                    outline: none;
                    color: #2d3748;
                }

                .search-input::placeholder {
                    color: #a0aec0;
                }

                .search-clear-btn {
                    background: none;
                    border: none;
                    color: #a0aec0;
                    padding: 8px;
                    cursor: pointer;
                    transition: color 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .search-clear-btn:hover {
                    color: #e53e3e;
                }

                .search-results-badge {
                    margin-left: 8px;
                    padding: 4px 12px;
                    font-size: 0.75rem;
                    flex-shrink: 0;
                }

                /* Styles des cartes en mode liste */
                .service-card-list {
                    display: flex !important;
                    flex-direction: row !important;
                    align-items: stretch !important;
                    gap: 1.5rem !important;
                }

                .service-card-list .service-image-container {
                    width: 200px !important;
                    min-height: 200px !important;
                    flex-shrink: 0 !important;
                }

                .service-card-list .service-content {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                }

                /* Animations */
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .services-grid .service-card {
                    animation: fadeInUp 0.4s ease forwards;
                }

                .services-list .service-card {
                    animation: fadeInUp 0.4s ease forwards;
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .header-content {
                        flex-direction: column;
                        align-items: stretch !important;
                        gap: 1rem;
                    }

                    .header-actions {
                        flex-wrap: wrap;
                        justify-content: center;
                    }

                    .view-toggle {
                        order: 2;
                    }

                    .modern-create-btn {
                        order: 1;
                        width: 100%;
                        justify-content: center;
                    }

                    .search-wrapper {
                        padding: 0 12px;
                    }

                    .search-input {
                        font-size: 0.85rem;
                        padding: 10px 0;
                    }

                    .service-card-list {
                        flex-direction: column !important;
                    }

                    .service-card-list .service-image-container {
                        width: 100% !important;
                        min-height: 200px !important;
                    }

                    .quick-stats {
                        flex-wrap: wrap;
                    }

                    .search-results-badge {
                        font-size: 0.65rem;
                        padding: 2px 8px;
                    }
                }

                @media (max-width: 480px) {
                    .service-card .service-image-container {
                        height: 180px;
                    }

                    .service-name {
                        font-size: 1rem;
                    }

                    .service-actions {
                        flex-direction: column;
                    }

                    .service-actions .action-btn {
                        width: 100%;
                    }
                }
            `}</style>
        </>
    );
};

export default ServiceManagementPage;